/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as AdaptiveCards from 'adaptivecards';
import 'adaptivecards/dist/adaptivecards-carousel.css';
import * as adaptivecardsTemplating from 'adaptivecards-templating';
import * as MarkdownIt from 'markdown-it';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Theme } from '../../../common/enums/Theme';
import { ApprovalRequestType } from '../../../data/types.generated';
import { MobileAdaptiveCardComponent } from '../../../mobile/components/MobileAdaptiveCard/MobileAdaptiveCard';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ErrorPage } from '../../containers/ErrorPage';
import { AdaptiveCardComponent, AdaptiveCardComponentProps } from './AdaptiveCard';
import { CharacterLimitMessage } from './CharacterLimitMessage';
import { setActiveApprovalType } from './elements/ApprovalTypeScope';
import { registerBadgeCardElement } from './elements/BadgeCardElement';
import { registerBorderedContainer } from './elements/BorderedContainer';
import { hostConfigContrast } from './hostConfigs/HostConfigContrast';

import './styles/AdaptiveCard.scss';
import { hostConfigDark } from './hostConfigs/HostConfigDark';
import { hostConfigDefault } from './hostConfigs/HostConfigDefault';

// The SDK marks its modules as side-effect-free. Reference these stock elements explicitly
// so webpack retains their implementations and registrations, not just the core elements.
AdaptiveCards.GlobalRegistry.elements.register('Table', AdaptiveCards.Table, AdaptiveCards.Versions.v1_5);
AdaptiveCards.GlobalRegistry.elements.register('Carousel', AdaptiveCards.Carousel, AdaptiveCards.Versions.v1_6);

// Registers custom element overrides needed to visually match the Teams-hosted renderer:
// - "Badge", a Teams-native element not implemented by the OSS SDK.
// - Container's "showBorder"/"roundedCorners", Teams-native renderer extensions not in the OSS SDK.
registerBadgeCardElement();
registerBorderedContainer();

export interface AdaptiveCardProps {
    approvalType: string;
    themeName: Theme;
    template: any;
    dataPayload: any;
    openUrlHandler: (href: string, id: string, title: string) => void;
    submitHandler: (url: string | undefined, data: any) => void;
}

function destroyCarousels(card: AdaptiveCards.AdaptiveCard): void {
    const visit = (element: AdaptiveCards.CardElement): void => {
        if (element instanceof AdaptiveCards.Carousel) {
            // The SDK owns the Swiper privately, but exposes its host element.
            // Swiper's public DOM API attaches the instance here and clears it on destroy.
            const host = element.carouselPageContainer as (HTMLElement & { swiper?: { destroy(): void } }) | undefined;
            host?.swiper?.destroy();
        }
        if (element instanceof AdaptiveCards.CardElementContainer) {
            for (let index = 0; index < element.getItemCount(); index++) {
                visit(element.getItemAt(index));
            }
        }
    };

    visit(card);
    // getAllActions includes descendant actions; ShowCards have their own element/action tree.
    for (const action of card.getAllActions()) {
        if (action instanceof AdaptiveCards.ShowCardAction) {
            destroyCarousels(action.card);
        }
    }
}

export const AdaptiveCardContainer: React.FC<AdaptiveCardProps> = (props: AdaptiveCardProps): JSX.Element | null => {
    const { approvalType, themeName, template, dataPayload, submitHandler, openUrlHandler } = props;
    const [content, setContent] = React.useState<JSX.Element | null>(null);

    const hostConfig = React.useMemo((): any => {
        switch (themeName) {
            case Theme.Dark:
                return hostConfigDark;
            case Theme.Contrast:
                return hostConfigContrast;
            default:
                return hostConfigDefault;
        }
    }, [themeName]);

    React.useLayoutEffect((): void | (() => void) => {
        const adaptiveCard = new AdaptiveCards.AdaptiveCard();
        const dispose = (): void => destroyCarousels(adaptiveCard);
        adaptiveCard.maxVersion = AdaptiveCards.Versions.v1_6;

        // Scopes the Badge/BorderedContainer overrides (registered globally against the SDK, see
        // registerBadgeCardElement/registerBorderedContainer above) to this parse+render pass so
        // only the Agent and App Approvals card picks up their custom rendering.
        setActiveApprovalType(approvalType);

        try {
            AdaptiveCards.AdaptiveCard.onProcessMarkdown = (
                text: string,
                result: AdaptiveCards.IMarkdownProcessingResult
            ): void => {
                result.outputHtml = MarkdownIt().render(text);
                result.didProcess = true;
            };

            adaptiveCard.onExecuteAction = (action: AdaptiveCards.Action): void => {
                const type = action.getJsonTypeName();
                if (type === 'Action.OpenUrl') {
                    openUrlHandler((action as AdaptiveCards.OpenUrlAction).url, action.id, action.title);
                } else if (type === 'Action.Submit') {
                    const data = (action as AdaptiveCards.SubmitAction).data as { url?: string };
                    submitHandler(data?.url, data);
                }
            };

            const templatePayload = new adaptivecardsTemplating.Template(template);

            // Expand the template with your `$root` data object
            // This binds it to the data and produces the final Adaptive Card payload
            const cardPayload = templatePayload.expand({
                $root: dataPayload,
            });

            adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(hostConfig);
            adaptiveCard.parse(cardPayload);

            const result = adaptiveCard.render();
            if (!result) {
                dispose();
                setContent(<ErrorPage />);
                return;
            }

            // Remove tab focus
            result.tabIndex = -1;

            const acSelectableElements = result.getElementsByClassName('ac-selectable');
            Array.from(acSelectableElements).forEach((acSelectableElement: Element): void => {
                (acSelectableElement as HTMLElement).style.cursor = 'Pointer';
            });

            // added aria label to links in adaptive card
            const anchorElements = result.getElementsByClassName('ac-anchor');
            Array.from(anchorElements).forEach((anchorElement: Element): void => {
                const anchorTitle = anchorElement.getAttribute('title');
                anchorElement.setAttribute('aria-label', anchorTitle);
            });

            // sets aria-expanded state for expand/collapse buttons
            const expandElements = result.getElementsByClassName('ac-selectable');
            Array.from(expandElements).forEach((expandElement: Element): void => {
                const elementTitle = expandElement.getAttribute('title') ?? expandElement.getAttribute('alt');
                if (elementTitle?.toLowerCase().includes('expand')) {
                    expandElement.setAttribute('aria-expanded', 'false');
                } else if (elementTitle?.toLowerCase().includes('collapse')) {
                    expandElement.setAttribute('aria-expanded', 'true');
                }
            });

            // setting alt to empty string for decorative images
            const imageElements = result.getElementsByClassName('ac-image');
            Array.from(imageElements).forEach((imageElement: Element): void => {
                const imageAlt = imageElement.getAttribute('alt');
                if (!imageAlt) {
                    imageElement.setAttribute('alt', '');
                }
                // addressing expand/collapse buttons that are images instead of selectables
                if (imageAlt?.toLowerCase().includes('expand')) {
                    imageElement.setAttribute('aria-expanded', 'false');
                } else if (imageAlt?.toLowerCase().includes('collapse')) {
                    imageElement.setAttribute('aria-expanded', 'true');
                }

                // The renderer attaches click handlers to non-selectable image wrappers, so screen readers announce "clickable".
                const imageWrapper = imageElement.parentElement;
                if (imageWrapper) {
                    const wrapperHasInteractiveRole = imageWrapper.hasAttribute('role');
                    const wrapperIsTabbable = imageWrapper.hasAttribute('tabindex') && imageWrapper.tabIndex >= 0;

                    // Only clear handlers when the wrapper is not meant to be interactive.
                    if (!wrapperHasInteractiveRole && !wrapperIsTabbable) {
                        imageWrapper.onclick = null;
                        imageWrapper.onkeypress = null;
                    }
                }
            });

            // Agent/app card only: show a message when the comment box hits its character limit.
            let limitMessagePortal: React.ReactPortal = null;
            if (approvalType === ApprovalRequestType.AgentAndAppApprovals) {
                const commentsInput = adaptiveCard.getElementById('approverComments') as AdaptiveCards.TextInput;

                if (commentsInput?.maxLength && commentsInput.renderedElement) {
                    const limitMessageHost = document.createElement('div');
                    limitMessageHost.className = 'ac-input-charLimit-message';
                    limitMessageHost.setAttribute('role', 'status');
                    limitMessageHost.setAttribute('aria-live', 'polite');
                    commentsInput.renderedElement.insertAdjacentElement('afterend', limitMessageHost);

                    limitMessagePortal = ReactDOM.createPortal(
                        <CharacterLimitMessage input={commentsInput} maxLength={commentsInput.maxLength} />,
                        limitMessageHost
                    );
                }
            }

            const childProps: AdaptiveCardComponentProps = {
                approvalType,
                themeName,
                adaptiveCardResult: result,
            };

            setContent(
                <>
                    {getAppHost().isMobile() ? (
                        <MobileAdaptiveCardComponent {...childProps} />
                    ) : (
                        <AdaptiveCardComponent {...childProps} />
                    )}
                    {limitMessagePortal}
                </>
            );
        } catch (error) {
            dispose();
            setContent(<ErrorPage message={error?.message} />);
        }
        return dispose;
    }, [approvalType, themeName, template, dataPayload, submitHandler, openUrlHandler, hostConfig]);

    return content;
};
